import { Bounce, toast } from "react-toastify";
import { CSSProperties, Dispatch, SetStateAction } from "react";
import { PickerConfiguration } from "react-google-drive-picker/dist/typeDefs";
import {
  checkGoogleAccessTokenValidity,
  getNewAccessAndRefreshTokens,
} from "../services/auth.service";
import { Auth } from "../auth/auth";
import {
  adminClauseCheckConstants,
  PromptsForFeature,
  UserRolesKey,
} from "../modules/Admin/constants";
import {
  ADMIN_ACTIONS,
  getClauseCheckSummaryListData,
  getClauseCheckSummaryListDataForContractReview,
} from "../modules/Admin/store/admin.thunk";
import {
  ClauseFunction,
  ClauseFunctionParams,
  ExecuteAllClauseInterface,
  IDriveView,
} from "./types";
import { hiddenPagesForEnvironments, LOCAL_STORAGE_KEYS } from "./constants";
import { getDefaultDriveDirectory } from "../services/common.service";
import {
  ISystemMessage,
  IUploadedDocToTTC,
  IUserBotMessage,
  vmoBotType,
} from "../modules/vscDashboard/types";
import { addDocExtentionIfMissing } from "../modules/vscDashboard/utils";
import { getMimeTypeAsPerFileExtension } from "../modules/vscDashboard/constants";
import { ProcessedFileForTTC } from "../modules/vscDashboard/model";
import { isString } from "underscore";

export const getFirstTwoInitialsOfName = (name: string, firstTwo?: boolean) => {
  const nameSplit = name?.split(" ");
  const wordsLength = nameSplit?.length;
  let str = "";
  if (wordsLength > 1) {
    str += nameSplit[0][0];
    if (firstTwo) {
      str += nameSplit[1][0];
    }
  } else if (wordsLength === 1) {
    str += nameSplit[0][0];
    if (firstTwo) {
      str += nameSplit[0][1];
    }
  }
  return str.toUpperCase();
};

export const parseJWTToken = (token: string) => {
  if (!token) return null;
  const [, body] = token.split(".");
  if (!body || body === undefined) return null;

  try {
    return JSON.parse(atob(body));
  } catch (e) {
    return null;
  }
};

export const filterKeys = (object: any, keys: string[]) => {
  let result: any = {};
  keys.forEach((key) => {
    if (object.hasOwnProperty(key)) {
      result[key] = object[key];
    }
  });
  return result;
};

export const changeFileType = (fileName: string, type: string) => {
  const words = fileName?.split(".");
  words.pop();
  const reName = words.join() + `.${type}`;
  return reName;
};

export const openFileInNewTab = (url: string) => {
  if (!url) return;
  return window.open(url, "_blank", "noopener,noreferrer");
};

export const downloadOrEnforceOpenFileInNewTab = async ({
  url,
  file_name,
  onlyOpen = false,
}: {
  url: string;
  file_name?: string;
  onlyOpen?: boolean;
}) => {
  if (!url) return;

  try {
    if (onlyOpen) {
      return openFileInNewTab(url);
    } else {
      const response = await fetch(url, { mode: "cors" });
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = file_name || "downloaded_file";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    }
  } catch {
    // fallback: open in new tab if download fails
    return openFileInNewTab(url);
  }
};

export const downloadFileFromSignedUrl = (url: string) => {
  if (!url) return;
  // For signed URLs (GCS, S3), directly open in new tab
  // This bypasses CORS issues and uses the browser's native download handling
  return openFileInNewTab(url);
};

export const downloadFileFromBlob = (
  url: string = "",
  filename: string = ""
) => {
  if (!url || !filename) return;
  const req = new XMLHttpRequest();
  req.open("GET", url, true);
  req.setRequestHeader("Access-Control-Allow-Origin", "*");
  req.responseType = "blob";
  req.onload = function () {
    const blob = new Blob([req.response], {
      type: "application/pdf",
    });

    if ((window.navigator as any).msSaveBlob) {
      (window.navigator as any).msSaveBlob(blob, filename);
    } else {
      const windowUrl = window.URL || window.webkitURL;
      const href = windowUrl.createObjectURL(blob);
      const a = document.createElement("a");
      a.setAttribute("download", filename);
      a.setAttribute("href", href);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };
  req.send();
};

export const getToast = (type: string, message: any) => {
  // Map caller-supplied label to react-toastify's variant.
  //   success  → success (green)
  //   info     → info    (blue)
  //   warning  → warning (amber) — used for validation-style messages
  //              that aren't hard errors (empty required input,
  //              partial success with failures).
  //   anything else → error (red)
  //
  // Case-normalized so both "Success" and "success" work — existing
  // call sites use a mix (e.g. `getToast("info", ...)` in Table.tsx,
  // `getToast("Success", ...)` elsewhere) and the old strict-case
  // check silently rendered every lowercase call as red error.
  const normalized = (type || "").toLowerCase();
  const toastType =
    normalized === "success" ? "success" :
    normalized === "info"    ? "info"    :
    normalized === "warning" ? "warning" :
    "error";
  toast[toastType](message, {
    transition: Bounce,
    theme: "colored",
    hideProgressBar: true,
    closeButton: false,
    icon: false,
  });
};

export const getTotalCommentsNumber = (comments: any) => {
  let totalComments: number = 0;
  Object.keys(comments).forEach((key) => {
    totalComments += comments?.[key]?.length;
  });
  return totalComments;
};

export const getRefreshedTokensAndUpdateThePreviousTokens = async () => {
  try {
    const auth = Auth.getInstance();
    const { access_token, refresh_token } = await getNewAccessAndRefreshTokens({
      refresh_token: auth.getGoogleRefreshToken() || "",
      redirect_uri: process.env.REACT_APP_GOOGLE_AUTH_REDIRECT_URI || "",
    });
    auth.saveGoogleAccessToken(access_token);
    auth.saveGoogleRefreshToken(refresh_token);
  } catch (error) {
    // The Google Drive-picker token is a separate concern from the Django
    // session — a hiccup refreshing it (network blip, Google API error,
    // revoked Drive consent) must not end an otherwise-valid app session.
    // Drive Picker features may be degraded until the next refresh cycle,
    // but the user should not be logged out because of this.
    console.error("Failed to refresh Google Drive token:", error);
  }
};

export const checkTokenValidityAndUpdateIfExpired = async () => {
  try {
    const auth = Auth.getInstance();
    const tokenInfo = await checkGoogleAccessTokenValidity(
      auth.getGoogleAccessToken() || ""
    );
    if (tokenInfo?.expires_in < 30) {
      await getRefreshedTokensAndUpdateThePreviousTokens();
    }
  } catch (error) {
    await getRefreshedTokensAndUpdateThePreviousTokens();
    console.error(error);
  }
};

const getDefaultDirectoryParams = async () => {
  const defaultParams: IDriveView[] = [];
  try {
    const res = await getDefaultDriveDirectory();
    if (res?.folders?.length === 0) return defaultParams;

    const views = res?.folders?.filter(
      (driveView: IDriveView) =>
        driveView?.drive_dir_id && driveView?.drive_dir_name
    );

    return views;
  } catch (error) {
    return defaultParams;
  }
};

declare const google: any;

export const handleDriveUploadCommonMethod = async (params: {
  setLoader: Dispatch<SetStateAction<boolean>>;
  openPicker: (config: PickerConfiguration) => boolean | undefined;
  multiselect?: boolean;
  onResponse?: Function;
  mimeTypes?: any;
  viewId: "DOCS" | "FOLDERS";
  parentId?: string;
  parentFolderName?: string;
  customView?: boolean;
  canSelectFolder: boolean;
}) => {
  params?.setLoader(true);
  await checkTokenValidityAndUpdateIfExpired();
  const auth = Auth.getInstance();

  let Views = [
    new google.picker.DocsView(google.picker.ViewId?.[params?.viewId])
      .setIncludeFolders(true)
      .setOwnedByMe(false)
      .setSelectFolderEnabled(params?.canSelectFolder)
      .setMimeTypes(params.mimeTypes)
      .setLabel("Shared with Me"),
    new google.picker.DocsView(google.picker.ViewId?.[params?.viewId])
      .setIncludeFolders(true)
      .setOwnedByMe(true)
      .setSelectFolderEnabled(params?.canSelectFolder)
      .setMimeTypes(params.mimeTypes)
      .setLabel("My Drive"),
  ];

  const customViews = await getDefaultDirectoryParams();
  const customViewBuilders = customViews?.map((driveView: IDriveView) =>
    new google.picker.DocsView(google.picker.ViewId?.[params?.viewId])
      .setIncludeFolders(true)
      .setSelectFolderEnabled(params?.canSelectFolder)
      .setMimeTypes(params.mimeTypes)
      .setParent(driveView?.drive_dir_id)
      .setLabel(driveView?.drive_dir_name)
  );
  Views = (customViewBuilders || []).concat(Views);

  // it's ok open the picker
  params?.setLoader(false);
  params?.openPicker({
    clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID || "",
    developerKey: process.env.REACT_APP_GOOGLE_API_KEY || "",
    token: auth.getGoogleAccessToken() || "",
    viewId: params.viewId,
    showUploadView: false,
    showUploadFolders: true,
    setIncludeFolders: true,
    supportDrives: true,
    multiselect: params?.multiselect,
    viewMimeTypes: params?.mimeTypes,
    customViews: Views,
    disableDefaultView: true,
    setSelectFolderEnabled: true,
    callbackFunction: async (res: any) =>
      params?.onResponse && params?.onResponse(res),
  });
};

export const getTextToCopy = (text: string | string[]) => {
  if (isString(text)) return text;
  if (!Array.isArray(text)) return "";
  return text
    ?.map((text) => {
      if (isString(text)) return text;
      return Object.entries(text)
        .map(([key, value]) => `${key}\n${value}`)
        .join("\n");
    })
    .join("\n\n");
};

// Utility to detect any table-related markup
const hasTableLikeContent = (html: string | null | undefined) => {
  if (!html) return false;
  const temp = document.createElement("div");
  temp.innerHTML = html;
  return !!temp.querySelector("table, thead, tbody, tr, td, th");
};

export function copyToClip(params: {
  setLoader?: Dispatch<SetStateAction<boolean>>;
  innerHTML: string;
  value?: string;
  onSuccess?: () => void;
  onError?: (err: any) => void;
}): void {
  try {
    params?.setLoader && params?.setLoader(true);
    const hasTableContent = hasTableLikeContent(params?.innerHTML);
    let blob = null;
    let clipboardItem;
    if (hasTableContent) {
      blob = new Blob([params?.innerHTML], { type: "text/html" });
      clipboardItem = new window.ClipboardItem({ "text/html": blob });
    } else if (params.value) {
      blob = new Blob([params.value], { type: "text/plain" });
      clipboardItem = new window.ClipboardItem({ "text/plain": blob });
    }
    clipboardItem && navigator.clipboard.write([clipboardItem]);
    params?.onSuccess && params?.onSuccess();
  } catch (error) {
    params?.onError && params?.onError(error);
  } finally {
    setTimeout(() => {
      params?.setLoader && params?.setLoader(false);
    }, 2000);
  }
}

export const getRoles = (roles: any, is_superuser?: boolean) => {
  if (is_superuser) {
    return "Admin";
  }
  if (Array.isArray(roles) && roles.length > 0) {
    const roleNames = roles.map((role: any) => role?.name);
    
    // Check for power user
    if (roleNames.includes(UserRolesKey.POWER_USER)) {
      return "Super Admin";
    }
    
    // Check for generic user role mapped to consultant
    if (
      roleNames.includes(UserRolesKey.USER) ||
      (roleNames.includes(UserRolesKey.REDLINING_USER) &&
        roleNames.includes(UserRolesKey.CLAUSE_CHECK_USER))
    ) {
      return "Vendor Solutions Consultant - Risk & Contracts";
    }
    return roles[0]?.display_name;
  }
  return "N/A";
};

export const parseNextLine = (text: string) => {
  return text.replace(/(\\n)/g, "\n");
};

export const getArrayOfKeywords = (keywords: string) => {
  return keywords ? keywords.slice(2, -2).split("', '").filter(Boolean) : [];
};

export const checkIfInputIsAnArrayWithValues = (input: string[]) => {
  return Array.isArray(input) && input?.length > 0;
};

export const sortByNameAlphabetically = (data: any) => {
  if (!checkIfInputIsAnArrayWithValues(data)) return [];
  return data?.sort((a: any, b: any) => a?.name?.localeCompare(b?.name));
};

export const checkEnabledEntity = (data: any) => {
  return checkIfInputIsAnArrayWithValues(data)
    ? data?.map(({ is_enabled }: any) => is_enabled)?.filter(Boolean)
    : [];
};

export const getClauseFunctionsLookup: Record<string, ClauseFunction> = {
  // Get Call for contract review list
  [adminClauseCheckConstants.CONTRACT_REVIEW_CLAUSE_LIST]: ({
    verticalId,
    isGlobal,
    contractTypeId,
  }: ClauseFunctionParams) => {
    if (!verticalId) return null;

    return getClauseCheckSummaryListDataForContractReview({
      verticalId,
      isGlobal,
      contractTypeId,
      feature:
        PromptsForFeature.CONTRACT_REVIEW_TAB.CONTRACT_REVIEW_CLAUSE_CHECK_LIST,
      type: ADMIN_ACTIONS.GET_CLAUSE_CHECK_SUMMARY_FOR_CONTRACT_REVIEW,
    });
  },

  // Get Call for clause check list
  [adminClauseCheckConstants.CLAUSE_CHECK_LIST]: ({
    verticalId,
  }: ClauseFunctionParams) => {
    if (!verticalId) return null;

    return getClauseCheckSummaryListData({
      verticalId,
      feature: PromptsForFeature.CLAUSE_CHECK_TAB.CLAUSE_CHECK,
      type: ADMIN_ACTIONS.GET_CLAUSE_CHECK_SUMMARY,
    });
  },

  // Get Call for import clause list
  [adminClauseCheckConstants.IMPORT_CLAUSE_CHECK_LIST]: ({
    verticalId,
  }: ClauseFunctionParams) => {
    if (!verticalId) return null;

    return getClauseCheckSummaryListData({
      verticalId,
      feature:
        PromptsForFeature.CLAUSE_CHECK_TAB.GET_IMPORT_CLAUSE_CHECK_SUMMARY,
      type: ADMIN_ACTIONS.GET_IMPORT_CLAUSE_CHECK_SUMMARY,
    });
  },
  // Get Call for contract review import clause list
  [adminClauseCheckConstants.IMPORT_CONTRACT_REVIEW_CLAUSE_CHECK_LIST]: ({
    verticalId,
    isGlobal,
    contractTypeId,
  }: ClauseFunctionParams) => {
    if (!verticalId) return null;

    return getClauseCheckSummaryListDataForContractReview({
      verticalId,
      isGlobal,
      contractTypeId,
      feature:
        PromptsForFeature.CONTRACT_REVIEW_TAB
          .CONTRACT_REVIEW_IMPORT_CLAUSE_CHECK_LIST,
      type: ADMIN_ACTIONS.GET_CONTRACT_REVIEW_IMPORT_CLAUSE_CHECK_LIST,
    });
  },
};

export const executeAllClauseFunctions = ({
  keys,
  verticalId,
  dispatch,
  isGlobal,
  contractTypeId,
}: ExecuteAllClauseInterface) => {
  const results: Record<string, any> = {};

  for (const [key, func] of Object.entries(getClauseFunctionsLookup)) {
    if (keys?.includes(key)) {
      results[key] = func({ verticalId, isGlobal, contractTypeId });
    }
  }

  Object.entries(results).forEach(([key, result]) => {
    if (result !== null) {
      dispatch(result);
    }
  });
};

export const truncateString = (text: string, length: number) => {
  return text?.length > length ? `${text?.slice(0, length)}...` : text;
};

export const normalizePaginationUrl = (url: string) => {
  if (!url) return url;
  try {
    const parsed = new URL(url);
    let path = parsed.pathname + parsed.search;
    // Remove leading /api if present
    if (path.startsWith("/api/")) {
      path = path.replace(/^\/api/, "");
    }
    return path;
  } catch {
    // If already relative, remove leading /api if present
    if (url.startsWith("/api/")) {
      return url.replace(/^\/api/, "");
    }
    return url;
  }
};

export const getNumberSuffix = (number: number) => {
  return number.toLocaleString("en-US", {
    maximumFractionDigits: 2,
    notation: "compact",
    compactDisplay: "short",
  });
};

export const isHiddenComponentsEnvironment =
  window.location.hostname ===
  process.env.REACT_APP_HIDDEN_COMPONENT_ENVIRONMENT_HOSTNAME;

// List of hostnames where the page should be hidden
export const hiddenEnvironments = [
  process.env.REACT_APP_HIDDEN_COMPONENT_ENVIRONMENT_HOSTNAME,
];

export const checkComponentVisibilityInHiddenEnv = (headerText: string) => {
  const isHiddenEnv = hiddenEnvironments.includes(window.location.hostname);
  return hiddenPagesForEnvironments.includes(headerText) && isHiddenEnv;
};

export const getFutureYears = (count: number) => {
  const currentYear = new Date().getFullYear();
  const futureYears = [];
  for (let i = 0; i < count; i++) {
    futureYears.push(currentYear + i);
  }
  return futureYears;
};

export const getUpdatedDocsData = (docs: any, data: IUploadedDocToTTC[]) => {
  const updatedProcessedDocs = docs.map((doc: any) => {
    const updatedData = data.find((item) => item?.file_id === doc?.id);
    return {
      ...(updatedData
        ? {
            ...updatedData,
            mimeType: doc?.mimeType,
            file_name: doc?.name,
            file_name_with_extension: addDocExtentionIfMissing(doc),
          }
        : {}),
    };
  });

  const processedFileIds = data.map((item) => item?.file_id);
  const filteredUploadedDocs = docs?.map((doc: any) => {
    const updatedData = data.find((item) => item?.file_id === doc?.id);
    return {
      ...(processedFileIds?.includes(doc?.id)
        ? {
            ...doc,
            id: updatedData?.id,
          }
        : {}),
    };
  });

  return { updatedProcessedDocs, filteredUploadedDocs };
};

export const getUpdatedDocsDataForLocal = (data: any) => {
  const updatedProcessedDocs = data?.map((doc: any) => ({
    ...doc,
    mimeType: getMimeTypeAsPerFileExtension(doc?.file_name),
    file_name_with_extension: addDocExtentionIfMissing({
      name: doc?.file_name,
    }),
  }));
  return { updatedProcessedDocs, filteredUploadedDocs: [] };
};

export const getFileNamesFromSelectedFileIds = (
  selectedFiles: string[],
  processedFiles: any
) => {
  const getRelevantFileNames = (key: string, fallbackKey: string) => {
    return selectedFiles
      .map((fileId) => {
        const file = processedFiles.find((doc: any) => doc?.id === fileId);
        if (file) {
          return file?.[key] ? file?.[key] : file?.[fallbackKey];
        }
      })
      .filter(Boolean);
  };
  return {
    fileNames: getRelevantFileNames("file_name_with_extension", "file_name"),
    gcsFileNames: getRelevantFileNames(
      "gcs_file_name",
      "file_name_with_extension"
    ),
  };
};

export const getSanitizedChatMessages = (
  messages: Array<ISystemMessage | IUserBotMessage>
) => {
  const sanitized: Array<any> = [];
  let systemBuffer: ISystemMessage[] = [];
  let processedFiles: IUploadedDocToTTC[] = [];

  const flushSystemBuffer = () => {
    if (systemBuffer.length === 0) return;
    // Club consecutive system messages into one, joining their responses
    const combinedText = systemBuffer.map((msg) => {
      processedFiles.push(new ProcessedFileForTTC(msg.ttc_documents));
      return {
        ...msg.ttc_documents,
        mimeType: getMimeTypeAsPerFileExtension(msg.ttc_documents?.file_name),
        name: msg.ttc_documents?.file_name,
      };
    });
    sanitized.push({
      sender: "system",
      text: combinedText,
      id: systemBuffer[0].id, // or generate a new id if needed
    });
    systemBuffer = [];
  };

  for (const msg of messages) {
    if (msg.role === "system") {
      systemBuffer.push(msg as ISystemMessage);
    } else if (msg.role === "user_llm") {
      flushSystemBuffer();
      const userMsg = msg as IUserBotMessage;
      // User message
      sanitized.push({
        sender: "user",
        text: userMsg.query,
        id: userMsg.id,
      });
      // Bot message
      sanitized.push({
        sender: "bot",
        text: userMsg.response,
        id: userMsg.id,
      });
    }
  }
  flushSystemBuffer();
  return {
    chat: sanitized,
    processedFiles: processedFiles,
    selectedFiles: processedFiles.map((file) => file.id),
  };
};

export const getIndexOfSystemMessageFromEnd = (chat: vmoBotType["chat"]) => {
  if (!chat || chat.length === 0) return -1;
  for (let i = chat.length - 1; i >= 0; i--) {
    if (chat[i].sender === "system") {
      return i;
    }
  }
  return -1;
};

export const getInitialVerticals = () => {
  const defaultVertical = {
    label: "",
    value: "",
  };

  const verticalsFromLocalStorage = JSON.parse(
    localStorage.getItem(LOCAL_STORAGE_KEYS.VERTICALS) || "{}"
  );

  return {
    [PromptsForFeature.CLAUSE_CHECK_TAB.CLAUSE_CHECK_VERTICAL_LIST]:
      verticalsFromLocalStorage?.[
        PromptsForFeature.CLAUSE_CHECK_TAB.CLAUSE_CHECK_VERTICAL_LIST
      ] || defaultVertical,

    [PromptsForFeature.CLAUSE_CHECK_TAB.CLAUSE_CHECK_VERTICAL_ADVANCED_LIST]:
      verticalsFromLocalStorage?.[
        PromptsForFeature.CLAUSE_CHECK_TAB.CLAUSE_CHECK_VERTICAL_ADVANCED_LIST
      ] || defaultVertical,

    [PromptsForFeature.CONTRACT_REDLINING]:
      verticalsFromLocalStorage?.[PromptsForFeature.CONTRACT_REDLINING] ||
      defaultVertical,

    [PromptsForFeature.CONTRACT_EXECUTION]:
      verticalsFromLocalStorage?.[PromptsForFeature.CONTRACT_EXECUTION] ||
      defaultVertical,

    [PromptsForFeature.CONTRACT_EXPIRY]:
      verticalsFromLocalStorage?.[PromptsForFeature.CONTRACT_EXPIRY] ||
      defaultVertical,

    [PromptsForFeature.TALK_TO_CONTRACT]:
      verticalsFromLocalStorage?.[PromptsForFeature.TALK_TO_CONTRACT] ||
      defaultVertical,
  };
};

export const localFileExists = ({
  processedFilesNames,
  fileName,
  files,
}: {
  processedFilesNames: string[];
  fileName: string;
  files: any[];
}) => {
  if (Array.isArray(processedFilesNames) && processedFilesNames?.length > 0) {
    return {
      exists: processedFilesNames.includes(fileName),
      alreadyExists: true,
    };
  }
  const map = new Map();
  files.forEach((fileObj) => {
    const fileName = fileObj?.file?.name;
    if (map.has(fileName)) {
      const val = map.get(fileName);
      map.set(fileName, val + 1);
    } else {
      map.set(fileName, 1);
    }
  });

  if (map.has(fileName)) {
    const val = map.get(fileName);
    if (val > 1) {
      return { exists: true, alreadyExists: false }; // File exists more than once
    }
  }
  return { exists: false, alreadyExists: false };
};

export const driveFileExists = ({
  processedFilesIds,
  file,
  driveFiles,
}: {
  processedFilesIds: string[];
  file: any;
  driveFiles: any[];
}) => {
  if (Array.isArray(processedFilesIds) && processedFilesIds?.length > 0) {
    return {
      exists: processedFilesIds.includes(file?.id),
      alreadyExists: true,
    };
  }

  const map = new Map();
  driveFiles.forEach((fileObj) => {
    const fileId = fileObj?.id;
    if (map.has(fileId)) {
      const val = map.get(fileId);
      map.set(fileId, val + 1);
    } else {
      map.set(fileId, 1);
    }
  });

  if (map.has(file?.id)) {
    const val = map.get(file?.id);
    if (val > 1) {
      return { exists: true, alreadyExists: false };
    }
  }
  return { exists: false, alreadyExists: false };
};
